/**
 * main.jsx — application entry point.
 *
 * Wires all top-level providers in the correct order:
 *
 *   ReduxProvider         ← needed by ThemeProvider (reads language) and useAuth
 *     ↓
 *   QueryClientProvider   ← TanStack Query for server state
 *     ↓
 *   ThemeProvider         ← MUI + emotion cache + direction
 *     ↓
 *   App                   ← router + global hooks
 *
 * i18n is initialized as a side-effect of importing `@/i18n`.
 *
 * Order matters: Redux must wrap everything that reads UI state,
 * QueryClient must wrap everything that uses useQuery, ThemeProvider
 * must wrap any MUI components.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './App';
import { store } from '@/store';
import { queryClient } from '@/services/query/queryClient';
import ThemeProvider from '@/theme/ThemeProvider';
// Self-hosted fonts (bundled — no external CDN, works on offline hosts).
import '@fontsource-variable/vazirmatn';
import '@fontsource-variable/plus-jakarta-sans';

// Initialize i18n (side-effect import — sets up i18next instance)
import '@/i18n';

// A clinic capture link (/face-capture?invite=…) may be opened while signed out;
// the route guard would redirect to /login and drop the query. Capture it here,
// at boot, before React Router mounts, so login can return the user to it.
try {
  const params = new URLSearchParams(window.location.search);
  const invite = params.get('invite');
  if (invite && window.location.pathname.startsWith('/face-capture')) {
    sessionStorage.setItem('pendingCaptureInvite', invite);
  }
  // Same for a support-sent re-run link (/rerun/<id>), which arrives by SMS
  // and is therefore usually opened on a device that is signed out.
  const rerun = window.location.pathname.match(/^\/rerun\/([^/]+)/);
  if (rerun) {
    sessionStorage.setItem('pendingRerunId', rerun[1]);
  }
} catch { /* ignore */ }

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <App />
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ReduxProvider>
  </React.StrictMode>
);
